import { Link, useParams } from 'react-router-dom'

import { fetchFip, fetchPrinciples, fetchResourceTypes } from '@/api'
import {
    BackLink,
    Chip,
    DefinitionItem,
    ErrorState,
    Loading,
    PageShell,
    PrincipleHint,
    Section,
    StatCard,
    StatusChip,
    Term,
    Tooltip,
    VerificationList,
} from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import type { DeclarationDetail, Principle, PrincipleGroup } from '@/types'

const groups: PrincipleGroup[] = ['F', 'A', 'I', 'R']

function DeclarationRow({
    principle,
    declarations,
    icons,
}: {
    principle: Principle
    declarations: DeclarationDetail[]
    icons: Map<string, string>
}) {
    const { t, localized } = useI18n()

    return (
        <div className="declaration">
            <div className="declaration__principle">
                <Tooltip content={<PrincipleHint principle={principle} />} focusable>
                    <span className="declaration__code">{principle.id}</span>
                </Tooltip>
                <span className="declaration__title">{localized(principle.title)}</span>
            </div>
            <div className="declaration__items">
                {declarations.length ? (
                    declarations.map((declaration, index) => (
                        <div className="declaration__item" key={`${declaration.resource}-${index}`}>
                            <span className="item__icon">
                                {icons.get(declaration.resource_detail?.type ?? '') ?? '•'}
                            </span>
                            <Link to={`/resources/${declaration.resource}`}>
                                {declaration.resource_detail?.name ?? declaration.resource}
                            </Link>
                            <StatusChip status={declaration.status} />
                            {declaration.note ? (
                                <p className="declaration__note">{localized(declaration.note)}</p>
                            ) : null}
                        </div>
                    ))
                ) : (
                    <p className="empty">{t('fip.gap')}</p>
                )}
            </div>
        </div>
    )
}

export function ProfileDetail() {
    const { fipId = '' } = useParams()
    const { t, localized, formatDate, groupName } = useI18n()
    const fip = useAsync(() => fetchFip(fipId), [fipId])
    const principles = useAsync(fetchPrinciples, [])
    const types = useAsync(fetchResourceTypes, [])

    if (fip.error) {
        return (
            <PageShell>
                <BackLink to="/profiles" label={t('nav.profiles')} />
                <ErrorState error={fip.error} onRetry={fip.reload} />
            </PageShell>
        )
    }
    if (!fip.data || !principles.data) {
        return (
            <PageShell>
                <Loading />
            </PageShell>
        )
    }

    const data = fip.data
    const allPrinciples = principles.data
    const icons = new Map((types.data ?? []).map((type) => [type.id, type.icon]))
    const byPrinciple = new Map<string, DeclarationDetail[]>()
    for (const declaration of data.declarations_detail) {
        const current = byPrinciple.get(declaration.principle) ?? []
        current.push(declaration)
        byPrinciple.set(declaration.principle, current)
    }

    return (
        <PageShell>
            <BackLink to="/profiles" label={t('nav.profiles')} />

            <div className="detail__head">
                <div className="chips" style={{ marginBottom: '0.5rem' }}>
                    <Chip variant={data.status === 'published' ? 'in-use' : 'under-consideration'}>
                        {t(`fip.status.${data.status}`)}
                    </Chip>
                    <Chip variant="outline">{localized(data.domain)}</Chip>
                </div>
                <h1>{localized(data.title)}</h1>
                <p className="lead">{localized(data.summary)}</p>
            </div>

            <div className="grid grid--stats">
                <StatCard
                    value={`${data.principle_count}/${allPrinciples.length}`}
                    label={t('fip.principlesCovered')}
                />
                <StatCard value={data.resource_count} label={t('fip.resourcesUsed')} />
                <StatCard value={data.declarations.length} label={t('fip.declarations')} />
            </div>

            <div className="card" style={{ marginTop: '1rem' }}>
                <dl className="definition">
                    {data.community_detail ? (
                        <DefinitionItem term={t('fip.community')} help="community">
                            {data.community_detail.url ? (
                                <a
                                    href={data.community_detail.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                >
                                    {localized(data.community_detail.name)}
                                </a>
                            ) : (
                                localized(data.community_detail.name)
                            )}
                        </DefinitionItem>
                    ) : null}
                    <DefinitionItem term={t('fip.version')}>v{data.version}</DefinitionItem>
                    <DefinitionItem term={t('fip.updated')}>
                        {formatDate(data.updated)}
                    </DefinitionItem>
                    {data.url ? (
                        <DefinitionItem term={t('common.website')}>
                            <a href={data.url} target="_blank" rel="noreferrer noopener">
                                {new URL(data.url).hostname}
                            </a>
                        </DefinitionItem>
                    ) : null}
                </dl>
                {data.community_detail ? (
                    <p className="item__desc" style={{ marginBottom: 0, marginTop: '0.9rem' }}>
                        {localized(data.community_detail.description)}
                    </p>
                ) : null}
            </div>

            <Section
                title={t('verification.title')}
                hint={t('verification.hint')}
                term="verification"
            >
                <div className="card">
                    <VerificationList items={data.verification_detail} />
                </div>
            </Section>

            <h2 style={{ marginTop: '2rem' }}>
                {t('fip.declarations')} <Term id="declaration" />
            </h2>
            <p className="section__hint" style={{ marginBottom: '1rem' }}>
                {t('fip.declarationsHint')}
            </p>
            {groups.map((group) => {
                const groupPrinciples = allPrinciples.filter((item) => item.group === group)
                return (
                    <div className="group" key={group}>
                        <div className="group__head">
                            <span className={`group__badge group__badge--${group}`}>{group}</span>
                            <h3 style={{ margin: 0 }}>{groupName(group)}</h3>
                            <span className="section__hint">{t(`glossary.group.${group}`)}</span>
                        </div>
                        <div className="card">
                            {groupPrinciples.map((principle) => (
                                <DeclarationRow
                                    key={principle.id}
                                    principle={principle}
                                    declarations={byPrinciple.get(principle.id) ?? []}
                                    icons={icons}
                                />
                            ))}
                        </div>
                    </div>
                )
            })}
        </PageShell>
    )
}
